import './style.css';
import {Map, View} from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import ImageWMS from 'ol/source/ImageWMS';
import ImageTile from 'ol/source/ImageTile.js';
import {defaults as defaultControls} from 'ol/control/defaults.js';
import FullScreen from 'ol/control/FullScreen.js';
import LayerSwitcher from 'ol-ext/control/LayerSwitcher.js';
import LayerGroup from 'ol/layer/Group.js';
import ol_legend_Legend from 'ol-ext/legend/Legend.js';
import ol_legend_Image from 'ol-ext/legend/Image.js';
import ol_control_Legend from 'ol-ext/control/Legend.js';
import {toStringHDMS} from 'ol/coordinate.js';
import {toLonLat} from 'ol/proj.js';
import Overlay from 'ol/Overlay.js';
import TileWMS from 'ol/source/TileWMS.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import WKT from 'ol/format/WKT.js';
import Style from 'ol/style/Style.js';
import Fill from 'ol/style/Fill.js';
import Stroke from 'ol/style/Stroke.js';
import {$} from 'jquery';

////////////////////////////////////////////////////////////////////////////////
// ZONAL STATISTICS CONFIGURATION
////////////////////////////////////////////////////////////////////////////////

const GEOSERVER_PUBLIC =
    'https://geoserver.eeza.csic.es/geoserver';

const GEOSERVER_INTERNAL =
    'http://geoserver.eeza.csic.es:1234/geoserver';

const PROVINCIAS_LAYER =
    'provincias:recintos_provinciales_inspire_peninbal_etrs89';

const CUENCAS_LAYER =
    'provincias:Cuencasesok4258';

const WPS_URL =
    `${GEOSERVER_PUBLIC}/ows`;

////////////////////////////////////////////////////////////////////////////////
// PROVINCES
////////////////////////////////////////////////////////////////////////////////

const provinciasQuerySource = new TileWMS({
    url: `${GEOSERVER_PUBLIC}/provincias/wms`,
    params: {
        'LAYERS': PROVINCIAS_LAYER,
        'TILED': true
    },
    serverType: 'geoserver',
    crossOrigin: 'anonymous'
});

const provinciasBoundarySource = new TileWMS({
    url: `${GEOSERVER_PUBLIC}/provincias/wms`,
    params: {
        'LAYERS': PROVINCIAS_LAYER,
        'STYLES': 'provincias_transparente',
        'FORMAT': 'image/png',
        'TRANSPARENT': true,
        'TILED': true
    },
    serverType: 'geoserver',
    crossOrigin: 'anonymous'
});

const provinciasBoundaryLayer = new TileLayer({
    source: provinciasBoundarySource,
    name: 'Límites provinciales',
    visible: true
});

provinciasBoundaryLayer.type_layer = 'TileLayer';
provinciasBoundaryLayer.set('zonalZoneType', 'provincias');
provinciasBoundaryLayer.set('zonalNameField', 'NAMEUNIT');
provinciasBoundaryLayer.set('zonalQuerySource', provinciasQuerySource);

////////////////////////////////////////////////////////////////////////////////
// BASINS
////////////////////////////////////////////////////////////////////////////////

const CUENCAS_NAME_FIELD = 'nom_rio_1';

const cuencasQuerySource = new TileWMS({
    url: `${GEOSERVER_PUBLIC}/provincias/wms`,
    params: {
        'LAYERS': CUENCAS_LAYER,
        'TILED': true
    },
    serverType: 'geoserver',
    crossOrigin: 'anonymous'
});

const cuencasBoundarySource = new TileWMS({
    url: `${GEOSERVER_PUBLIC}/provincias/wms`,
    params: {
        'LAYERS': CUENCAS_LAYER,
        'STYLES': 'provincias_transparente',
        'FORMAT': 'image/png',
        'TRANSPARENT': true,
        'TILED': true
    },
    serverType: 'geoserver',
    crossOrigin: 'anonymous'
});

const cuencasBoundaryLayer = new TileLayer({
    source: cuencasBoundarySource,
    name: 'Límites de cuencas hidrográficas',
    visible: false
});

cuencasBoundaryLayer.type_layer = 'TileLayer';
cuencasBoundaryLayer.set('zonalZoneType', 'cuencas');
cuencasBoundaryLayer.set('zonalNameField', CUENCAS_NAME_FIELD);
cuencasBoundaryLayer.set('zonalQuerySource', cuencasQuerySource);

////////////////////////////////////////////////////////////////////////////////
// SELECTED ZONE HIGHLIGHT
////////////////////////////////////////////////////////////////////////////////

const selectedZoneStyle = new Style({
    fill: new Fill({
        color: 'rgba(255, 255, 255, 0)'
    }),
    stroke: new Stroke({
        color: '#000000',
        width: 4
    })
});

const selectedZoneSource = new VectorSource();

const selectedZoneLayer = new VectorLayer({
    source: selectedZoneSource,
    style: selectedZoneStyle,
    name: 'Zona seleccionada',
    visible: true
});

selectedZoneLayer.type_layer = 'VectorLayer';
selectedZoneLayer.set('zonalSelectionLayer', true);
selectedZoneLayer.set('displayInLayerSwitcher', false);

////////////////////////////////////////////////////////////////////////////////
// MUTUAL EXCLUSIVITY
////////////////////////////////////////////////////////////////////////////////

provinciasBoundaryLayer.on('change:visible', evt => {
    if (evt.target.getVisible()) {
        cuencasBoundaryLayer.setVisible(false);
    }
    clearSelectedZone();
});

cuencasBoundaryLayer.on('change:visible', evt => {
    if (evt.target.getVisible()) {
        provinciasBoundaryLayer.setVisible(false);
    }
    clearSelectedZone();
});

////////////////////////////////////////////////////////////////////////////////
// ZONAL ZONE CONFIGURATION
////////////////////////////////////////////////////////////////////////////////

const ZONAL_ZONES = {
    provincias: {
        type: 'provincias',
        title: 'Provincia',
        layer: PROVINCIAS_LAYER,
        nameField: 'NAMEUNIT',
        querySource: provinciasQuerySource
    },
    cuencas: {
        type: 'cuencas',
        title: 'Cuenca hidrográfica',
        layer: CUENCAS_LAYER,
        nameField: CUENCAS_NAME_FIELD,
        querySource: cuencasQuerySource
    }
};

function getActiveZonalZone() {
    const allLayers = map.getAllLayers();

    for (let i = allLayers.length - 1; i >= 0; i--) {
        const layer = allLayers[i];
        const zoneType = layer.get('zonalZoneType');

        if (
            layer.isVisible() &&
            zoneType &&
            ZONAL_ZONES[zoneType]
        ) {
            return {
                layer,
                config: ZONAL_ZONES[zoneType]
            };
        }
    }

    return null;
}

////////////////////////////////////////////////////////////////////////////////
// LEGEND STRUCTURE
////////////////////////////////////////////////////////////////////////////////

var legend = new ol_legend_Legend({
    title: 'Leyenda'
});

var legendCtrl = new ol_control_Legend({
    legend,
    collapse: false
});

////////////////////////////////////////////////////////////////////////////////
// LAYER CREATION FUNCTION
////////////////////////////////////////////////////////////////////////////////

function createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
) {
    let source = new TileWMS({
        url: wmsurl,
        params: {
            layers: layer_name,
            tiled: true
        },
        serverType: 'geoserver',
        crossOrigin: null
    });

    source.type_source = type_source;

    let layer = new TileLayer({
        source,
        name,
        extent,
        visible: false
    });

    layer.set('zonalStatsEnabled', true);
    layer.set('zonalCoverageId', layer_name.replace(':', '__'));
    layer.type_layer = type_layer;

    if (downloadpath !== '') {
        layer.downloadpath = downloadpath;
    }

    if (information !== '') {
        layer.info = information;
    }

    if (wms !== '') {
        layer.wms = wms;
    }

    if (boollegend === true) {
        var leyenda = new ol_legend_Legend({layer});

        leyenda.addItem(
            new ol_legend_Image({
                title: name,
                src: source.getLegendUrl(null, {layer: layer_name})
            })
        );

        legend.addItem(leyenda);
    }

    return layer;
}

////////////////////////////////////////////////////////////////////////////////
// ATLAS DESERTIFICACION
////////////////////////////////////////////////////////////////////////////////

var wmsurl =
    'https://geoserver.eeza.csic.es/geoserver/Desertificación/wms';

var layer_name = 'Desertificación:degradacionp50';
var type_source = 'TileWMS';
var type_layer = 'Image';
var name = 'Degradación P.50';
var extent = [
    -2020991.9743565675,
    3203134.121141932,
    481008.0256434325,
    5433134.121141932
];
var information =
    'Probabilidad de degradación mayor al 50% (Atlas de la Desertificación de España)';
var downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20y%20Degradaci%c3%b3n/Degradaci%c3%b3n%20mayor%20al%2050%25.zip';
var wms = '';
var boollegend = true;

var degrada50 = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// DEGRADATION 70
////////////////////////////////////////////////////////////////////////////////

layer_name = 'Desertificación:degradacionp70';
name = 'Degradación P.70';
information =
    'Probabilidad de degradación mayor al 70% (Atlas de la Desertificación de España)';
downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20y%20Degradaci%c3%b3n/Degradaci%c3%b3n%20mayor%20al%2070%25.zip';

var degrada70 = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// DEGRADATION 90
////////////////////////////////////////////////////////////////////////////////

layer_name = 'Desertificación:degradacionp90';
name = 'Degradación P.90';
information =
    'Probabilidad de degradación mayor al 90% (Atlas de la Desertificación de España)';
downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20y%20Degradaci%c3%b3n/Degradaci%c3%b3n%20mayor%20al%2090%25.zip';

var degrada90 = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// DESERTIFICATION 50
////////////////////////////////////////////////////////////////////////////////

layer_name = 'Desertificación:desertificacionp50';
name = 'Desertificación P.50';
information =
    'Probabilidad de desertificación mayor al 50% (Atlas de la Desertificación de España)';
downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20mayor%20al%2050%25.zip';

var desert50 = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// DESERTIFICATION 70
////////////////////////////////////////////////////////////////////////////////

layer_name = 'Desertificación:desertificacionp70';
name = 'Desertificación P.70';
information =
    'Probabilidad de desertificación mayor al 70% (Atlas de la Desertificación de España)';
downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20mayor%20al%2070%25.zip';

var desert70 = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// DESERTIFICATION 90
////////////////////////////////////////////////////////////////////////////////

layer_name = 'Desertificación:desertificacionp90';
name = 'Desertificación P.90';
information =
    'Probabilidad de desertificación mayor al 90% (Atlas de la Desertificación de España)';
downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20mayor%20al%2070%25.zip';

var desert90 = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// UMBRALES DEGRADACION
////////////////////////////////////////////////////////////////////////////////

layer_name = 'Desertificación:umbrales_degradacion';
name = 'Umbrales degradacion';
information =
    'Umbrales (50%, 70%, y 90%) de degradación considerados (Atlas de la Desertificación de España)';
downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20y%20Degradaci%c3%b3n/Umbrales%20degradaci%c3%b3n.zip';

var umbrales_degrad = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// UMBRALES DESERTIFICACION
////////////////////////////////////////////////////////////////////////////////

layer_name = 'Desertificación:umbrales_desertificacion';
name = 'Umbrales desertificación';
information =
    'Umbrales (50%, 70%, y 90%) de desertificación considerados (Atlas de la Desertificación de España)';
downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20y%20Degradaci%c3%b3n/Umbrales%20desertificaci%c3%b3n.zip';

var umbrales_desert = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// PROBABILIDAD DEGRADACION
////////////////////////////////////////////////////////////////////////////////

layer_name = 'Desertificación:probabilidad_degradación';
name = 'Probabilidad degradación';
information =
    'Probabilidad de degradación devuelta por el modelo. Es importante hacer notar que no se trata de una probabilidad de que un pixel se degrade, sino de la probabilidad de que el modelo clasifique el pixel como degradado (Atlas de la Desertificación de España)';
downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20y%20Degradaci%c3%b3n/Probabilidad%20degradaci%c3%b3n.zip';

var prob_degrad = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// INCERTIDUMBRE DEGRADACION
////////////////////////////////////////////////////////////////////////////////

layer_name = 'Desertificación:std_degradacion';
name = 'Incertidumbre degradación';
information =
    'Incertidumbre del modelo de degradación. Es la desviación estándar entre los resultados de cada modelo individual (Atlas de la Desertificación de España)';
downloadpath =
    'https://geoserver.eeza.csic.es/ftp/Desertificaci%c3%b3n/Desertificaci%c3%b3n%20y%20Degradaci%c3%b3n/Incertidumbre%20degradaci%c3%b3n.zip';

var std_degrad = createFullTiledWMSLayer(
    wmsurl,
    layer_name,
    type_source,
    type_layer,
    name,
    extent,
    information,
    downloadpath,
    boollegend
);

////////////////////////////////////////////////////////////////////////////////
// GROUPS
////////////////////////////////////////////////////////////////////////////////

const lddgroup = new LayerGroup({
    title: 'Desertificación y degradación del territorio',
    openInLayerSwitcher: false,
    layers: [
        degrada90,
        degrada70,
        degrada50,
        desert90,
        desert70,
        desert50,
        umbrales_degrad,
        umbrales_desert,
        prob_degrad,
        std_degrad
    ],
    visible: true
});

lddgroup.wms =
    'http://geoserver.eeza.csic.es:1234/geoserver/testatlas/wms';
lddgroup.info = 'Atlas de la Desertificación en España';

const atlasdesert = new LayerGroup({
    title: 'Desertificación',
    openInLayerSwitcher: false,
    layers: [lddgroup],
    visible: true
});

////////////////////////////////////////////////////////////////////////////////
// BASE LAYERS
////////////////////////////////////////////////////////////////////////////////

const openstreetmap_source = new OSM();
openstreetmap_source.type_source = 'OSM';

const openstreetmap = new TileLayer({
    source: openstreetmap_source,
    name: 'OpenStreetMap',
    baseLayer: true
});

openstreetmap.type_layer = 'TileLayer';

const googlesatellite_source = new ImageTile({
    url: 'http://mt0.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}',
    crossOrigin: 'anonymous'
});

googlesatellite_source.type_source = 'ImageTile';

const googlesatellite = new TileLayer({
    source: googlesatellite_source,
    name: 'Google Satellite',
    baseLayer: true,
    visible: false
});

googlesatellite.type_layer = 'TileLayer';

const baselayers = new LayerGroup({
    title: 'Base Layers',
    openInLayerSwithcer: false,
    layers: [openstreetmap, googlesatellite],
    visible: true
});

////////////////////////////////////////////////////////////////////////////////
// MAIN LAYER ARRAY
////////////////////////////////////////////////////////////////////////////////

const layers = [
    baselayers,
    atlasdesert,
    provinciasBoundaryLayer,
    cuencasBoundaryLayer,
    selectedZoneLayer
];

////////////////////////////////////////////////////////////////////////////////
// VIEW
////////////////////////////////////////////////////////////////////////////////

const view = new View({
    center: [-680000, 4350000],
    zoom: 5
});

////////////////////////////////////////////////////////////////////////////////
// POPUP
////////////////////////////////////////////////////////////////////////////////

const container = document.getElementById('popup');
const content = document.getElementById('popup-content');
const closer = document.getElementById('popup-closer');

const overlay = new Overlay({
    element: container,
    autoPan: {
        animation: {
            duration: 250
        }
    }
});

content.classList.add('ol-popup-content');

////////////////////////////////////////////////////////////////////////////////
// MAP
////////////////////////////////////////////////////////////////////////////////

const map = new Map({
    controls: defaultControls().extend([
        new FullScreen()
    ]),
    target: 'map',
    layers,
    overlays: [overlay],
    view
});

////////////////////////////////////////////////////////////////////////////////
// SELECTED ZONE FUNCTIONS
////////////////////////////////////////////////////////////////////////////////

function clearSelectedZone() {
    selectedZoneSource.clear();
}

function highlightSelectedZone(zoneFeature) {
    clearSelectedZone();

    if (!zoneFeature) {
        return;
    }

    try {
        const format = new GeoJSON();

        const dataProjection =
            zoneFeature._featureProjection || 'EPSG:3857';

        const feature = format.readFeature(zoneFeature, {
            dataProjection,
            featureProjection: 'EPSG:3857'
        });

        selectedZoneSource.addFeature(feature);

        console.log(
            'Geometría seleccionada:',
            feature.getGeometry()
        );

        console.log(
            'Extent seleccionada:',
            feature.getGeometry().getExtent()
        );
    }
    catch (error) {
        console.error(
            'Error resaltando la zona seleccionada:',
            error
        );
    }
}

////////////////////////////////////////////////////////////////////////////////
// LAYER SWITCHERS
////////////////////////////////////////////////////////////////////////////////

const switcher = new LayerSwitcher({});
map.addControl(switcher);

var switcher2 = new LayerSwitcher({
    target: $('.layerSwitcher').get(0),
    show_progress: true,
    extent: true
});

////////////////////////////////////////////////////////////////////////////////
// INFORMATION POPUP
////////////////////////////////////////////////////////////////////////////////

const popupinfo = document.getElementById('custom-popupinfo');
const contentinfo =
    document.getElementById('custom-popupinfo-content');
const popupinfo_closer =
    document.getElementById('custom-popupinfo-closer');

contentinfo.classList.add('popupinfo-content');

popupinfo_closer.onclick = function () {
    popupinfo.style.visibility = 'hidden';
    contentinfo.style.visibility = 'hidden';
};

switcher2.on('drawlist', function (e) {
    var layer = e.layer;

    if (layer.info) {
        $('<div class="myInfo">')
            .text('i')
            .on('click', function () {

                let title = document.createElement('h3');
                title.className = 'title';

                let titlenomo =
                    layer.getProperties('name').name;

                if (typeof titlenomo === 'undefined') {
                    titlenomo =
                        layer.getProperties('title').title;
                }

                title.textContent = titlenomo;

                let texto = document.createElement('p');
                texto.className = 'information';
                texto.textContent = layer.info;

                while (contentinfo.hasChildNodes()) {
                    contentinfo.removeChild(
                        contentinfo.firstChild
                    );
                }

                contentinfo.appendChild(title);
                contentinfo.appendChild(texto);

                contentinfo.style.visibility = 'visible';
                popupinfo.style.visibility = 'visible';
            })
            .appendTo(
                $('> .ol-layerswitcher-buttons', e.li)
            );
    }
});

////////////////////////////////////////////////////////////////////////////////
// DOWNLOAD BUTTON IN LAYER SWITCHER
////////////////////////////////////////////////////////////////////////////////

switcher2.on('drawlist', function (e) {
    var layer = e.layer;
    const downloadpath = layer.downloadpath;

    if (downloadpath) {
        $('<div class="myDown">')
            .text('d')
            .click(function () {
                let a = document.createElement('a');
                a.href = downloadpath;
                a.click();
            })
            .appendTo(
                $('> .ol-layerswitcher-buttons', e.li)
            );
    }
});

////////////////////////////////////////////////////////////////////////////////
// WMS BUTTON
////////////////////////////////////////////////////////////////////////////////

switcher2.on('drawlist', function (e) {
    var layer = e.layer;
    const wmsLayerUrl = layer.wms;

    if (wmsLayerUrl) {
        $('<div class="myWMS">')
            .text('WMS')
            .click(function () {
                window.open(wmsLayerUrl, '_blank');
            })
            .appendTo(
                $('> .ol-layerswitcher-buttons', e.li)
            );
    }
});

map.addControl(switcher2);

////////////////////////////////////////////////////////////////////////////////
// LAYER SWITCHER HELPER
////////////////////////////////////////////////////////////////////////////////

function displayInLayerSwitcher(b) {
    if (typeof nearth !== 'undefined') {
        nearth.set('displayInLayerSwitcher', b);
    }
}

////////////////////////////////////////////////////////////////////////////////
// LEGEND
////////////////////////////////////////////////////////////////////////////////

map.addControl(legendCtrl);

////////////////////////////////////////////////////////////////////////////////
// POPUP CLOSE
////////////////////////////////////////////////////////////////////////////////

closer.onclick = function () {
    overlay.setPosition(undefined);
    closer.blur();
    return false;
};

////////////////////////////////////////////////////////////////////////////////
// POPUP CONTENT
////////////////////////////////////////////////////////////////////////////////

function prettyMultiPopup(nomoj, layernames, urls) {

    if (content.hasChildNodes()) {
        content.removeChild(content.firstChild);
    }

    var div = document.createElement('div');
    div.className = 'popup-outer-div';

    for (let idx = 0; idx < urls.length; idx++) {

        let divinner = document.createElement('div');
        divinner.className = 'popup-inner-div';

        let url = urls[idx];
        let nomo = nomoj[idx];
        let layername = layernames[idx];

        var title = document.createElement('h3');
        title.className = 'popup-filtitle';
        title.textContent = nomo;

        divinner.appendChild(title);

        let table = document.createElement('table');
        table.className = 'popup-table';

        if (url) {

            fetch(url)
                .then(response => response.text())
                .then(text => {

                    let vortaro = JSON.parse(text);

                    if (
                        !vortaro.features ||
                        vortaro.features.length === 0
                    ) {
                        return;
                    }

                    let prop =
                        vortaro.features[0].properties;

                    Object.keys(prop).forEach(key => {

                        let tr = document.createElement('tr');

                        let td_key =
                            document.createElement('td');

                        let td_value =
                            document.createElement('td');

                        if (key === 'GRAY_INDEX') {
                            td_key.textContent = nomo;
                        }
                        else {
                            td_key.textContent = key;
                        }

                        td_key.style.fontWeight = 'bold';

                        td_value.textContent =
                            fixMojibake(prop[key]);

                        tr.appendChild(td_key);
                        tr.appendChild(td_value);

                        table.appendChild(tr);
                    });

                    divinner.appendChild(table);
                })
                .catch(error => {
                    console.error(
                        'Error en popup:',
                        error
                    );
                });
        }

        div.appendChild(divinner);
    }

    content.appendChild(div);
}

////////////////////////////////////////////////////////////////////////////////
// POPUP ALL LAYERS
////////////////////////////////////////////////////////////////////////////////

function popupAllLayers(evt) {

    const viewResolution = view.getResolution();

    const testlayers = map.getAllLayers();

    var source = null;
    var nomo = null;
    var testlayer = null;
    var nomoj = [];
    var urls = [];
    var layernames = [];

    for (
        let idx = testlayers.length - 1;
        idx >= 0;
        idx--
    ) {

        testlayer = testlayers[idx];

        if (testlayer.isVisible()) {

            source = testlayer.getSource();

            if (!source) {
                continue;
            }

            if (testlayer.get('zonalSelectionLayer')) {
                continue;
            }

            nomo =
                testlayer.getProperties()['name'];

            if (
                source.type_source === 'ImageWMS' ||
                source.type_source === 'TileWMS'
            ) {

                const params = source.getParams();

                const layersParam =
                    params.layers || params.LAYERS;

                if (!layersParam) {
                    continue;
                }

                var layername =
                    layersParam
                        .split(':')
                        .slice(1)
                        .join(':');

                nomoj.push(nomo);
                layernames.push(layername);

                const url =
                    source.getFeatureInfoUrl(
                        evt.coordinate,
                        viewResolution,
                        'EPSG:3857',
                        {
                            'INFO_FORMAT':
                                'application/json',
                            'QUERY_LAYERS':
                                layersParam
                        }
                    );

                urls.push(url);
            }
        }
    }

    if (urls.length > 0) {

        prettyMultiPopup(
            nomoj,
            layernames,
            urls
        );

        overlay.setPosition(evt.coordinate);
    }
}

////////////////////////////////////////////////////////////////////////////////
// ZONAL STATISTICS PANEL
////////////////////////////////////////////////////////////////////////////////

const zonalStatsPanel =
    document.getElementById('zonal-stats');

const zonalStatsContent =
    document.getElementById('zonal-stats-content');

const zonalStatsCloser =
    document.getElementById('zonal-stats-closer');

zonalStatsCloser.onclick = function () {
    zonalStatsPanel.style.display = 'none';
};

////////////////////////////////////////////////////////////////////////////////
// ACTIVE RASTER / ACTIVE ZONE
////////////////////////////////////////////////////////////////////////////////

function getActiveZonalLayer() {

    const allLayers = map.getAllLayers();

    for (
        let i = allLayers.length - 1;
        i >= 0;
        i--
    ) {

        const layer = allLayers[i];

        if (
            layer.isVisible() &&
            layer.get('zonalStatsEnabled') === true &&
            layer.get('zonalCoverageId')
        ) {
            return layer;
        }
    }

    return null;
}

function getActiveZoneLayer() {
    return getActiveZonalZone()?.layer || null;
}

////////////////////////////////////////////////////////////////////////////////
// ESCAPE XML ATTRIBUTE
////////////////////////////////////////////////////////////////////////////////

function escapeXmlAttribute(value) {

    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

////////////////////////////////////////////////////////////////////////////////
// ESCAPE XML TEXT
////////////////////////////////////////////////////////////////////////////////

function escapeXmlText(value) {

    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

////////////////////////////////////////////////////////////////////////////////
// FORMATTING
////////////////////////////////////////////////////////////////////////////////

function formatStat(value, decimals = 3) {

    const number = Number(value);

    if (!Number.isFinite(number)) {
        return 'Sin datos';
    }

    return number.toFixed(decimals);
}

function formatCount(value) {

    const number = Number(value);

    if (!Number.isFinite(number)) {
        return 'Sin datos';
    }

    return number.toLocaleString('es-ES');
}

////////////////////////////////////////////////////////////////////////////////
// GET RASTER PIXEL INFORMATION
////////////////////////////////////////////////////////////////////////////////

async function getRasterPixelInfo(
    activeRaster,
    coordinate
) {

    if (!activeRaster) {
        return null;
    }

    const source =
        activeRaster.getSource();

    if (!source) {
        return null;
    }

    const params =
        source.getParams();

    const layersParam =
        params.layers ||
        params.LAYERS;

    if (!layersParam) {
        return null;
    }

    const resolution =
        view.getResolution();

    const url =
        source.getFeatureInfoUrl(
            coordinate,
            resolution,
            'EPSG:3857',
            {
                'INFO_FORMAT':
                    'application/json',

                'QUERY_LAYERS':
                    layersParam,

                'FEATURE_COUNT':
                    1
            }
        );

    if (!url) {
        return null;
    }

    console.log(
        'Raster pixel GetFeatureInfo URL:',
        url
    );

    const response =
        await fetch(url);

    if (!response.ok) {

        throw new Error(
            `Error consultando el valor del píxel: HTTP ${response.status}`
        );
    }

    const result =
        await response.json();

    console.log(
        'Información del píxel:',
        result
    );

    if (
        !result.features ||
        result.features.length === 0
    ) {
        return null;
    }

    return result.features[0].properties || {};
}

////////////////////////////////////////////////////////////////////////////////
// FIX MOJIBAKE
////////////////////////////////////////////////////////////////////////////////

function fixMojibake(value) {

    if (typeof value !== 'string') {
        return value;
    }

    if (!/[ÃÂâ]/.test(value)) {
        return value;
    }

    try {

        const bytes = Uint8Array.from(
            value,
            char => char.charCodeAt(0)
        );

        return new TextDecoder(
            'utf-8',
            {fatal: true}
        ).decode(bytes);

    }
    catch (error) {
        return value;
    }
}

////////////////////////////////////////////////////////////////////////////////
// SHOW ZONAL MESSAGE
////////////////////////////////////////////////////////////////////////////////

function showZonalMessage(title, message) {

    zonalStatsPanel.style.display = 'block';

    zonalStatsContent.innerHTML = `
        <h3>${title}</h3>
        <p class="zonal-loading">${message}</p>
    `;
}

////////////////////////////////////////////////////////////////////////////////
// SANITIZE FILE NAME
////////////////////////////////////////////////////////////////////////////////

function sanitizeFileName(value) {

    return String(value || 'seleccion')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
}

////////////////////////////////////////////////////////////////////////////////
// BUILD WCS URL FOR THE SELECTED ZONE BOUNDING BOX
////////////////////////////////////////////////////////////////////////////////

function buildRasterSubsetURL(activeRaster, extent) {

    if (
        !activeRaster ||
        !extent ||
        extent.length !== 4
    ) {

        throw new Error(
            'No se puede construir la URL WCS del bounding box.'
        );
    }

    const coverageId =
        activeRaster.get('zonalCoverageId');

    if (!coverageId) {

        throw new Error(
            'La capa raster no tiene zonalCoverageId.'
        );
    }

    const [
        minX,
        minY,
        maxX,
        maxY
    ] = extent;

    const params = new URLSearchParams({
        service: 'WCS',
        version: '2.0.1',
        request: 'GetCoverage',
        coverageId: coverageId,
        format: 'image/tiff'
    });

    /*
     * Estos son los nombres de eje que devuelve
     * DescribeCoverage para este GeoServer:
     *
     * axisLabels="X Y"
     *
     * No utilizar Long / Lat.
     */
    params.append(
        'subset',
        `X(${minX},${maxX})`
    );

    params.append(
        'subset',
        `Y(${minY},${maxY})`
    );

    const url =
        `${GEOSERVER_INTERNAL}/wcs?${params.toString()}`;

    console.log(
        'WCS subset URL:',
        url
    );

    console.log(
        'Coverage:',
        coverageId
    );

    console.log(
        'Bounding box:',
        extent
    );

    return url;
}

////////////////////////////////////////////////////////////////////////////////
// BUILD WPS CROPCOVERAGE REQUEST
////////////////////////////////////////////////////////////////////////////////

function buildCropCoverageXML(
    activeRaster,
    zoneSource
) {

    if (!activeRaster) {

        throw new Error(
            'No se ha seleccionado ninguna capa raster.'
        );
    }

    if (!zoneSource) {

        throw new Error(
            'No existe una zona seleccionada.'
        );
    }

    const coverageId =
        activeRaster.get('zonalCoverageId');

    if (!coverageId) {

        throw new Error(
            'La capa raster no tiene zonalCoverageId.'
        );
    }

    const selectedFeatures =
        zoneSource.getFeatures();

    if (
        !selectedFeatures ||
        selectedFeatures.length === 0
    ) {

        throw new Error(
            'No existe ninguna geometría seleccionada.'
        );
    }

    const feature =
        selectedFeatures[0];

    const geometry =
        feature.getGeometry();

    if (!geometry) {

        throw new Error(
            'La zona seleccionada no tiene geometría.'
        );
    }

    /*
     * La geometría seleccionada está en EPSG:3857.
     *
     * El raster también está en EPSG:3857.
     *
     * Por tanto podemos enviar directamente la geometría
     * al CropCoverage mediante EWKT.
     */
    const wktFormat = new WKT();

    const wkt =
        wktFormat.writeGeometry(
            geometry,
            {
                decimals: 3
            }
        );

    const ewkt =
        `SRID=3857;${wkt}`;

    console.log(
        'WKT de la zona seleccionada:',
        wkt
    );

    console.log(
        'EWKT enviado a CropCoverage:',
        ewkt
    );

    /*
     * Primero obtenemos el bounding box de la geometría.
     *
     * Esto mantiene la ventaja del WCS que ya sabemos que
     * funciona y evita enviar el raster completo al WPS.
     */
    const extent =
        geometry.getExtent();

    const wcsURL =
        buildRasterSubsetURL(
            activeRaster,
            extent
        );

    /*
     * El WKT NO se introduce en la URL.
     *
     * La geometría se introduce como ComplexData dentro
     * del POST WPS.
     *
     * CropCoverage acepta explícitamente:
     *
     * application/ewkt
     */
    const xml = `
<?xml version="1.0" encoding="UTF-8"?>

<wps:Execute
    service="WPS"
    version="1.0.0"
    xmlns:wps="http://www.opengis.net/wps/1.0.0"
    xmlns:ows="http://www.opengis.net/ows/1.1"
    xmlns:xlink="http://www.w3.org/1999/xlink">

    <ows:Identifier>
        gs:CropCoverage
    </ows:Identifier>

    <wps:DataInputs>

        <wps:Input>

            <ows:Identifier>
                coverage
            </ows:Identifier>

            <wps:Reference
                mimeType="image/tiff"
                xlink:href="${escapeXmlAttribute(wcsURL)}"
                method="GET"
            />

        </wps:Input>

        <wps:Input>

            <ows:Identifier>
                cropShape
            </ows:Identifier>

            <wps:Data>

                <wps:ComplexData
                    mimeType="application/ewkt"
                >${escapeXmlText(ewkt)}</wps:ComplexData>

            </wps:Data>

        </wps:Input>

    </wps:DataInputs>

    <wps:ResponseForm>

        <wps:RawDataOutput
            mimeType="image/tiff"
        >

            <ows:Identifier>
                result
            </ows:Identifier>

        </wps:RawDataOutput>

    </wps:ResponseForm>

</wps:Execute>
    `.trim();

    console.log(
        'WPS CropCoverage XML:',
        xml
    );

    return xml;
}

////////////////////////////////////////////////////////////////////////////////
// DOWNLOAD CROPPED RASTER
////////////////////////////////////////////////////////////////////////////////

async function downloadRasterCrop(
    activeRaster,
    zoneSource,
    zoneName,
    rasterName
) {

    const xml =
        buildCropCoverageXML(
            activeRaster,
            zoneSource
        );

    console.log(
        'Enviando CropCoverage a:',
        WPS_URL
    );

    const response =
        await fetch(
            WPS_URL,
            {
                method: 'POST',

                headers: {
                    'Content-Type':
                        'application/xml',

                    'Accept':
                        'image/tiff'
                },

                body: xml
            }
        );

    const contentType =
        response.headers.get('content-type') || '';

    console.log(
        'CropCoverage HTTP status:',
        response.status
    );

    console.log(
        'CropCoverage content-type:',
        contentType
    );

    /*
     * Si GeoServer devuelve un error XML, lo leemos como texto
     * para poder mostrar el mensaje real.
     */
    if (!response.ok) {

        const errorText =
            await response.text();

        throw new Error(
            `GeoServer CropCoverage HTTP ${response.status}:\n${errorText}`
        );
    }

    /*
     * Una ejecución correcta debe devolver TIFF.
     *
     * Si GeoServer devuelve XML, probablemente es un
     * ExceptionReport aunque HTTP sea 200.
     */
    if (
        contentType.includes('xml') ||
        contentType.includes('text')
    ) {

        const errorText =
            await response.text();

        throw new Error(
            `GeoServer CropCoverage devolvió una respuesta no TIFF:\n${errorText}`
        );
    }

    const blob =
        await response.blob();

    if (!blob || blob.size === 0) {

        throw new Error(
            'GeoServer devolvió un GeoTIFF vacío.'
        );
    }

    console.log(
        'GeoTIFF recibido:',
        blob.size,
        'bytes'
    );

    /*
     * Creamos una URL temporal para el Blob.
     *
     * Esto evita tener que construir una URL GET
     * con la geometría y, por tanto, evita el problema
     * Request-URI Too Long.
     */
    const blobURL =
        URL.createObjectURL(blob);

    const safeZone =
        sanitizeFileName(zoneName);

    const safeRaster =
        sanitizeFileName(rasterName);

    const filename =
        `${safeZone}_${safeRaster}_recortado.tif`;

    const link =
        document.createElement('a');

    link.href = blobURL;
    link.download = filename;

    document.body.appendChild(link);

    link.click();

    link.remove();

    /*
     * Dejamos un pequeño margen para que el navegador
     * complete la descarga antes de liberar el Blob URL.
     */
    setTimeout(() => {
        URL.revokeObjectURL(blobURL);
    }, 5000);

    console.log(
        'Descarga iniciada:',
        filename
    );
}

////////////////////////////////////////////////////////////////////////////////
// DISPLAY WPS RESULT
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// SHOW ZONAL STATISTICS
////////////////////////////////////////////////////////////////////////////////

function showZonalStatistics(
    zoneName,
    rasterName,
    properties,
    downloadHandler = null,
    pixelProperties = null
) {

    zonalStatsPanel.style.display = 'block';

    const downloadHTML =
        downloadHandler
            ? `
                <p class="zonal-download">
                    <button
                        type="button"
                        id="zonal-download-button"
                    >
                        Descargar GeoTIFF recortado
                    </button>
                </p>
            `
            : '';

    ////////////////////////////////////////////////////////////////////////
    // PIXEL INFORMATION
    ////////////////////////////////////////////////////////////////////////

    let pixelHTML = '';

    if (
        pixelProperties &&
        Object.keys(pixelProperties).length > 0
    ) {

        const pixelRows =
            Object.keys(pixelProperties)
                .map(key => {

                    let label = key;

                    /*
                     * GRAY_INDEX es el nombre habitual que devuelve
                     * GeoServer para el valor de un raster.
                     */
                    if (key === 'GRAY_INDEX') {
                        label = 'Valor del píxel';
                    }

                    const value =
                        pixelProperties[key];

                    let formattedValue =
                        value;

                    if (
                        typeof value === 'number'
                    ) {

                        formattedValue =
                            formatStat(value);
                    }

                    return `
                        <tr>
                            <td>${label}</td>
                            <td>${formattedValue}</td>
                        </tr>
                    `;
                })
                .join('');

        pixelHTML = `

            <div class="zonal-pixel-section">

                <div class="zonal-section-title">
                    Valor en el píxel seleccionado
                </div>

                <table class="zonal-pixel-table">
                    ${pixelRows}
                </table>

            </div>

        `;
    }
    else {

        pixelHTML = `

            <div class="zonal-pixel-section">

                <div class="zonal-section-title">
                    Valor en el píxel seleccionado
                </div>

                <p class="zonal-no-data">
                    No se ha podido obtener el valor del píxel.
                </p>

            </div>

        `;
    }

    ////////////////////////////////////////////////////////////////////////
    // PANEL
    ////////////////////////////////////////////////////////////////////////

    zonalStatsContent.innerHTML = `

        <h3>${zoneName}</h3>

        <p>
            <strong>Capa:</strong><br>
            ${rasterName}
        </p>

        <div class="zonal-section-title">
            Estadísticas de la zona
        </div>

        <table>

            <tr>
                <td>Media</td>
                <td>
                    ${formatStat(properties.avg)}
                </td>
            </tr>

            <tr>
                <td>Mínimo</td>
                <td>
                    ${formatStat(properties.min)}
                </td>
            </tr>

            <tr>
                <td>Máximo</td>
                <td>
                    ${formatStat(properties.max)}
                </td>
            </tr>

            <tr>
                <td>Desv. estándar</td>
                <td>
                    ${formatStat(properties.stddev)}
                </td>
            </tr>

            <tr>
                <td>Píxeles</td>
                <td>
                    ${formatCount(properties.count)}
                </td>
            </tr>

        </table>

        ${pixelHTML}

        ${downloadHTML}
    `;

    ////////////////////////////////////////////////////////////////////////
    // DOWNLOAD BUTTON
    ////////////////////////////////////////////////////////////////////////

    if (downloadHandler) {

        const downloadButton =
            document.getElementById(
                'zonal-download-button'
            );

        if (downloadButton) {

            downloadButton.addEventListener(
                'click',
                async function () {

                    downloadButton.disabled = true;

                    downloadButton.textContent =
                        'Generando GeoTIFF...';

                    try {

                        await downloadHandler();

                        downloadButton.textContent =
                            'GeoTIFF descargado';

                    }
                    catch (error) {

                        console.error(
                            'Error descargando GeoTIFF:',
                            error
                        );

                        downloadButton.disabled =
                            false;

                        downloadButton.textContent =
                            'Reintentar descarga';

                        let errorElement =
                            document.getElementById(
                                'zonal-download-error'
                            );

                        if (!errorElement) {

                            errorElement =
                                document.createElement('small');

                            errorElement.id =
                                'zonal-download-error';

                            errorElement.style.display =
                                'block';

                            errorElement.style.marginTop =
                                '8px';

                            errorElement.style.color =
                                '#b00020';

                            downloadButton
                                .parentElement
                                .appendChild(
                                    errorElement
                                );
                        }

                        errorElement.textContent =
                            error.message ||
                            'Error generando el GeoTIFF.';
                    }
                }
            );
        }
    }
}

////////////////////////////////////////////////////////////////////////////////
// GET ZONE NAME
////////////////////////////////////////////////////////////////////////////////

function getZoneName(
    feature,
    zoneConfig
) {

    const properties =
        feature.properties || {};

    if (zoneConfig.type === 'cuencas') {

        const possibleNameFields = [
            'nom_rio_1',
            'nom_rio_1_'
        ];

        for (
            const field of possibleNameFields
        ) {

            const rawName =
                properties[field];

            if (
                rawName !== null &&
                rawName !== undefined &&
                String(rawName).trim() !== ''
            ) {

                return fixMojibake(
                    String(rawName).trim()
                );
            }
        }

        if (feature.id) {
            return `Cuenca ${feature.id}`;
        }

        return 'Cuenca hidrográfica';
    }

    const rawName =
        properties[zoneConfig.nameField];

    if (
        rawName !== null &&
        rawName !== undefined &&
        String(rawName).trim() !== ''
    ) {

        return fixMojibake(
            String(rawName).trim()
        );
    }

    return zoneConfig.title;
}

////////////////////////////////////////////////////////////////////////////////
// GET ZONE AT COORDINATE
////////////////////////////////////////////////////////////////////////////////

async function getZoneAtCoordinate(
    coordinate,
    zoneConfig
) {

    const resolution =
        view.getResolution();

    const url =
        zoneConfig.querySource.getFeatureInfoUrl(
            coordinate,
            resolution,
            'EPSG:3857',
            {
                'INFO_FORMAT':
                    'application/json',

                'QUERY_LAYERS':
                    zoneConfig.layer,

                'FEATURE_COUNT':
                    1
            }
        );

    if (!url) {
        return null;
    }

    console.log(
        'GetFeatureInfo URL:',
        url
    );

    const response =
        await fetch(url);

    if (!response.ok) {

        throw new Error(
            `Error consultando ${zoneConfig.title}: HTTP ${response.status}`
        );
    }

    const result =
        await response.json();

    if (
        !result.features ||
        result.features.length === 0
    ) {
        return null;
    }

    const feature =
        result.features[0];

    let featureProjection =
        'EPSG:3857';

    if (
        result.crs &&
        result.crs.properties &&
        result.crs.properties.name
    ) {

        const crsName =
            result.crs.properties.name;

        if (crsName.includes('4326')) {

            featureProjection =
                'EPSG:4326';

        }
        else if (
            crsName.includes('3857')
        ) {

            featureProjection =
                'EPSG:3857';
        }
    }

    feature._featureProjection =
        featureProjection;

    console.log(
        'Feature seleccionada:',
        feature
    );

    console.log(
        'Feature ID:',
        feature.id
    );

    console.log(
        'Feature properties:',
        feature.properties
    );

    console.log(
        'Proyección de la geometría:',
        featureProjection
    );

    return feature;
}

////////////////////////////////////////////////////////////////////////////////
// BUILD WFS URLS
////////////////////////////////////////////////////////////////////////////////

function buildProvinceZonesURL(inspireId) {

    const safeId =
        String(inspireId)
            .replace(/'/g, "''");

    const wfsParams =
        new URLSearchParams({

            service: 'WFS',

            version: '1.1.0',

            request: 'GetFeature',

            typeName:
                PROVINCIAS_LAYER,

            CQL_FILTER:
                `INSPIREID='${safeId}'`,

            outputFormat:
                'application/json',

            srsName:
                'EPSG:4326'
        });

    return (
        `${GEOSERVER_INTERNAL}/provincias/ows?` +
        wfsParams.toString()
    );
}

function buildCuencaZonesURL(featureId) {

    if (!featureId) {

        throw new Error(
            'La feature de cuenca no tiene feature.id.'
        );
    }

    const wfsParams =
        new URLSearchParams({

            service: 'WFS',

            version: '1.1.0',

            request: 'GetFeature',

            typeName:
                CUENCAS_LAYER,

            featureID:
                String(featureId),

            outputFormat:
                'application/json',

            srsName:
                'EPSG:4326'
        });

    const wfsURL =
        `${GEOSERVER_INTERNAL}/provincias/ows?` +
        wfsParams.toString();

    console.log(
        'WFS URL cuenca:',
        wfsURL
    );

    return wfsURL;
}

////////////////////////////////////////////////////////////////////////////////
// BUILD WPS REQUEST
////////////////////////////////////////////////////////////////////////////////

function buildZonalStatisticsXML(
    zonesURL,
    coverageId
) {

    const wcsParams =
        new URLSearchParams({

            service: 'WCS',

            version: '2.0.1',

            request: 'GetCoverage',

            coverageId,

            format: 'image/tiff'
        });

    const wcsURL =
        `${GEOSERVER_INTERNAL}/wcs?` +
        wcsParams.toString();

    return `
<?xml version="1.0" encoding="UTF-8"?>

<wps:Execute
    service="WPS"
    version="1.0.0"
    xmlns:wps="http://www.opengis.net/wps/1.0.0"
    xmlns:ows="http://www.opengis.net/ows/1.1"
    xmlns:xlink="http://www.w3.org/1999/xlink">

    <ows:Identifier>
        gs:RasterZonalStatistics
    </ows:Identifier>

    <wps:DataInputs>

        <wps:Input>

            <ows:Identifier>
                data
            </ows:Identifier>

            <wps:Reference
                mimeType="image/tiff"
                xlink:href="${escapeXmlAttribute(wcsURL)}"
            />

        </wps:Input>

        <wps:Input>

            <ows:Identifier>
                band
            </ows:Identifier>

            <wps:Data>
                <wps:LiteralData>0</wps:LiteralData>
            </wps:Data>

        </wps:Input>

        <wps:Input>

            <ows:Identifier>
                zones
            </ows:Identifier>

            <wps:Reference
                mimeType="application/json"
                xlink:href="${escapeXmlAttribute(zonesURL)}"
                method="GET"
            />

        </wps:Input>

    </wps:DataInputs>

    <wps:ResponseForm>

        <wps:RawDataOutput
            mimeType="application/json"
        >

            <ows:Identifier>
                statistics
            </ows:Identifier>

        </wps:RawDataOutput>

    </wps:ResponseForm>

</wps:Execute>
    `.trim();
}

////////////////////////////////////////////////////////////////////////////////
// EXECUTE WPS
////////////////////////////////////////////////////////////////////////////////

async function runZonalStatistics(
    zonesURL,
    coverageId
) {

    const xml =
        buildZonalStatisticsXML(
            zonesURL,
            coverageId
        );

    const response =
        await fetch(
            WPS_URL,
            {
                method: 'POST',

                headers: {
                    'Content-Type':
                        'application/xml',

                    'Accept':
                        'application/json'
                },

                body: xml
            }
        );

    const text =
        await response.text();

    console.log(
        'WPS HTTP STATUS:',
        response.status
    );

    console.log(
        'WPS RESPONSE:',
        text
    );

    if (!response.ok) {

        throw new Error(
            `GeoServer HTTP ${response.status}: ${text}`
        );
    }

    if (
        text.includes('ExceptionReport') ||
        text.includes('ExceptionText')
    ) {

        throw new Error(
            `GeoServer WPS Exception:\n${text}`
        );
    }

    let result;

    try {

        result =
            JSON.parse(text);

    }
    catch (error) {

        throw new Error(
            `La respuesta WPS no es JSON:\n${text}`
        );
    }

    if (
        !result.features ||
        result.features.length === 0
    ) {

        throw new Error(
            'RasterZonalStatistics no devolvió ninguna feature.'
        );
    }

    return result.features[0].properties;
}

////////////////////////////////////////////////////////////////////////////////
// SINGLE CLICK
////////////////////////////////////////////////////////////////////////////////

//map.on(
//    'singleclick',
//    popupAllLayers
//);

map.on(
    'singleclick',
    async function (evt) {

        try {

            // 1. Determine active province/basin layer.

            const activeZone =
                getActiveZonalZone();

            if (!activeZone) {

                clearSelectedZone();

                zonalStatsPanel.style.display =
                    'none';

                return;
            }

            const zoneConfig =
                activeZone.config;

            console.log(
                'Zona activa:',
                zoneConfig.type
            );

            // 2. Determine active raster layer.

            const activeRaster =
                getActiveZonalLayer();

            if (!activeRaster) {

                showZonalMessage(
                    'Estadísticas zonales',
                    'Seleccione primero una capa raster del Atlas.'
                );

                return;
            }

            const coverageId =
                activeRaster.get(
                    'zonalCoverageId'
                );

            const rasterName =
                activeRaster.get('name') ||
                coverageId;

            // 3. Identify the selected province or basin.

            const zoneFeature =
                await getZoneAtCoordinate(
                    evt.coordinate,
                    zoneConfig
                );

            if (!zoneFeature) {

                clearSelectedZone();

                showZonalMessage(
                    'Estadísticas zonales',
                    `No se encontró ninguna ${
                        zoneConfig.type === 'cuencas'
                            ? 'cuenca'
                            : 'provincia'
                    } en ese punto.`
                );

                return;
            }

            // 4. Highlight the selected feature.

            highlightSelectedZone(
                zoneFeature
            );

            // 5. Get name.

            const zoneName =
                getZoneName(
                    zoneFeature,
                    zoneConfig
                );

            // 6. Show calculation status.

            showZonalMessage(
                zoneName,
                'Calculando estadísticas...'
            );

            // 7. Build WFS URL for the complete selected zone.

            let zonesURL;

            if (
                zoneConfig.type ===
                'provincias'
            ) {

                const properties =
                    zoneFeature.properties || {};

                const inspireId =
                    properties.INSPIREID;

                if (!inspireId) {

                    throw new Error(
                        'La feature provincial no contiene INSPIREID.'
                    );
                }

                zonesURL =
                    buildProvinceZonesURL(
                        inspireId
                    );

            }
            else if (
                zoneConfig.type ===
                'cuencas'
            ) {

                zonesURL =
                    buildCuencaZonesURL(
                        zoneFeature.id
                    );

            }
            else {

                throw new Error(
                    `Tipo de zona desconocido: ${zoneConfig.type}`
                );
            }

            console.log(
                'WFS zones URL:',
                zonesURL
            );

            console.log(
                'Raster seleccionado:',
                rasterName,
                coverageId
            );

            // 8. Execute WPS statistics.

            const statistics =
                await runZonalStatistics(
                    zonesURL,
                    coverageId
                );
	    // 8b. Get the value of the raster pixel at the
	    // exact location clicked by the user.

	   let pixelProperties = null;

	   try {

    		pixelProperties =
        		await getRasterPixelInfo(
            			activeRaster,
            			evt.coordinate
        		);

    	    console.log(
        	'Valor del píxel seleccionado:',
        	pixelProperties
    	    );

	    }
	    catch (pixelError) {

    		console.warn(
        		'No se pudo obtener la información del píxel:',
        		pixelError
    		);

    		pixelProperties = null;
	    }
            // 9. Prepare the crop download handler.
            //
            // Important:
            // We do NOT construct a GET URL containing WKT.
            //
            // The geometry remains in selectedZoneSource
            // and is sent later as application/ewkt inside
            // a WPS POST request.

            const downloadHandler =
                async function () {

                    /*
                     * Recheck the selected geometry before
                     * starting the download.
                     */
                    if (
                        selectedZoneSource
                            .getFeatures()
                            .length === 0
                    ) {

                        throw new Error(
                            'No hay ninguna geometría seleccionada para recortar.'
                        );
                    }

                    await downloadRasterCrop(
                        activeRaster,
                        selectedZoneSource,
                        zoneName,
                        rasterName
                    );
                };

            // 10. Show statistics + download button.

            showZonalStatistics(
                zoneName,
                rasterName,
                statistics,
                downloadHandler,
		pixelProperties
            );

            console.log(
                'Estadísticas zonales:',
                zoneName,
                zoneConfig.type,
                coverageId,
                statistics
            );
        }

        catch (error) {

            console.error(
                'Error en estadísticas zonales:',
                error
            );

            zonalStatsPanel.style.display =
                'block';

            zonalStatsContent.innerHTML = `

                <h3>
                    Estadísticas zonales
                </h3>

                <p>
                    No se pudieron calcular las estadísticas.
                </p>

                <small>
                    ${
                        error.message ||
                        'Consulta la consola del navegador para ver el error.'
                    }
                </small>
            `;
        }
    }
);

////////////////////////////////////////////////////////////////////////////////
// END
////////////////////////////////////////////////////////////////////////////////
